import { Logo } from "@/components/common/Logo";
import {
  forgotPassword,
  resetPassword,
  verifyResetToken,
} from "@/services/api/auth";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { App, Button, Form, Input } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./ResetPassword.less";

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const { message: messageApi } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const hasVerified = useRef(false); // 防止重复验证

  // 检查 URL 参数，判断是第一步（输入邮箱）还是第二步（输入密码）
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");
  const isStep2 = !!token; // 如果有 token，说明是第二步

  // 如果是第二步,通过 token 获取数据
  useEffect(() => {
    if (isStep2 && token && !hasVerified.current) {
      hasVerified.current = true; // 标记为已验证

      const verifyToken = async () => {
        try {
          const res = await verifyResetToken({ token });
          if (res.data.code === 200 && res.data.data?.valid) {
            // Token 有效,可以继续
            console.log("Token 验证成功,邮箱:", res.data.data.email);
          } else {
            messageApi.error(t("auth.resetPassword.token_invalid"));
            navigate("/auth/reset-password");
          }
        } catch (error) {
          messageApi.error(t("auth.resetPassword.token_invalid"));
          navigate("/auth/reset-password");
        }
      };
      verifyToken();
    }
  }, [isStep2, token, navigate, messageApi, t]);

  // 第一步:发送重置密码邮件
  const handleSendResetEmail = async (values: { email: string }) => {
    try {
      setLoading(true);
      const res = await forgotPassword({ email: values.email });

      // 根据安全要求,无论邮箱是否存在都显示成功消息
      if (res.data.code === 200) {
        messageApi.success(t("auth.resetPassword.email_sent"));
        setEmailSent(true);
      } else {
        messageApi.error(
          res.data.message || t("auth.resetPassword.send_email_failed")
        );
      }
    } catch (error: any) {
      messageApi.error(
        error?.message || t("auth.resetPassword.send_email_failed")
      );
    } finally {
      setLoading(false);
    }
  };

  // 第二步:重置密码
  const handleResetPassword = async (values: {
    password: string;
    confirmPassword: string;
  }) => {
    if (values.password !== values.confirmPassword) {
      messageApi.error(t("auth.common.validation_password_mismatch"));
      return;
    }

    try {
      setLoading(true);
      const res = await resetPassword({
        token: token!,
        new_password: values.password,
      });

      if (res.data.code === 200) {
        messageApi.success(t("auth.resetPassword.reset_success"));
        // 跳转到登录页面
        setTimeout(() => {
          navigate("/auth/login");
        }, 1500);
      } else {
        messageApi.error(
          res.data.message || t("auth.resetPassword.reset_failed")
        );
      }
    } catch (error: any) {
      messageApi.error(error?.message || t("auth.resetPassword.reset_failed"));
    } finally {
      setLoading(false);
    }
  };

  // 第一步：输入邮箱
  if (!isStep2) {
    return (
      <>
        {/* Logo 和标题 */}
        <div className="reset-password-header">
          <div className="reset-password-logo">
            <Logo height={50} />
          </div>
          <div className="reset-password-tagline">
            {t("auth.common.tagline")}
          </div>
        </div>

        {/* 欢迎信息 */}
        <div className="reset-password-welcome">
          <h1 className="welcome-title">
            {t("auth.resetPassword.welcome_title")}
          </h1>
          <p className="welcome-subtitle">
            {t("auth.resetPassword.welcome_subtitle")}
          </p>
        </div>

        {/* 第一步表单：输入邮箱 */}
        {emailSent ? (
          <div className="email-sent-message">
            <p>{t("auth.resetPassword.email_sent_message")}</p>
            <p className="email-sent-hint">
              {t("auth.resetPassword.email_sent_hint")}
            </p>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSendResetEmail}
            className="reset-password-form"
            size="large"
          >
            {/* 企业邮箱输入 */}
            <Form.Item
              name="email"
              label={t("auth.common.label_corporate_email")}
              rules={[
                {
                  required: true,
                  message: t("auth.common.validation_email_required"),
                },
                {
                  type: "email",
                  message: t("auth.common.validation_email_invalid"),
                },
              ]}
            >
              <Input
                prefix={<MailOutlined className="input-icon" />}
                placeholder={t("auth.common.placeholder_email")}
                className="reset-password-input"
              />
            </Form.Item>

            {/* 重置密码按钮 */}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="reset-password-button"
                block
              >
                {t("auth.resetPassword.btn_send_reset_email")}
              </Button>
            </Form.Item>
          </Form>
        )}

        {/* 返回登录链接 */}
        <div className="back-to-login">
          <Link to="/auth/login">{t("auth.resetPassword.back_to_login")}</Link>
        </div>
      </>
    );
  }

  // 第二步：输入新密码
  return (
    <>
      {/* Logo 和标题 */}
      <div className="reset-password-header">
        <div className="reset-password-logo">
          <span className="logo-text">GenOptima</span>
        </div>
        <div className="reset-password-tagline">{t("auth.common.tagline")}</div>
      </div>

      {/* 欢迎信息 */}
      <div className="reset-password-welcome">
        <h1 className="welcome-title">
          {t("auth.resetPassword.set_new_password_title")}
        </h1>
        <p className="welcome-subtitle">
          {t("auth.resetPassword.set_new_password_subtitle")}
        </p>
      </div>

      {/* 第二步表单：输入新密码 */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleResetPassword}
        className="reset-password-form"
        size="large"
      >
        {/* 新密码输入 */}
        <Form.Item
          name="password"
          label={t("auth.common.label_new_password")}
          rules={[
            {
              required: true,
              message: t("auth.common.validation_password_required"),
            },
            { min: 6, message: t("auth.common.validation_password_min") },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="input-icon" />}
            placeholder={t("auth.common.placeholder_password")}
            className="reset-password-input"
            iconRender={(visible) =>
              visible ? (
                <EyeOutlined className="password-toggle" />
              ) : (
                <EyeInvisibleOutlined className="password-toggle" />
              )
            }
          />
        </Form.Item>

        {/* 确认新密码输入 */}
        <Form.Item
          name="confirmPassword"
          label={t("auth.common.label_confirm_password")}
          dependencies={["password"]}
          rules={[
            {
              required: true,
              message: t("auth.common.validation_password_required"),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(t("auth.common.validation_password_mismatch"))
                );
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="input-icon" />}
            placeholder={t("auth.common.placeholder_password")}
            className="reset-password-input"
            iconRender={(visible) =>
              visible ? (
                <EyeOutlined className="password-toggle" />
              ) : (
                <EyeInvisibleOutlined className="password-toggle" />
              )
            }
          />
        </Form.Item>

        {/* 重置密码按钮 */}
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            className="reset-password-button"
            block
          >
            {t("auth.resetPassword.btn_reset_password")}
          </Button>
        </Form.Item>
      </Form>

      {/* 返回登录链接 */}
      <div className="back-to-login">
        <Link to="/auth/login">{t("auth.resetPassword.back_to_login")}</Link>
      </div>
    </>
  );
};

export default ResetPassword;
